import { ComponentPropsWithoutRef, createContext, useContext, useMemo } from 'react';
import { TransactionMethodPresentational } from 'assets/js/pages/transactions/TransactionMethod';
import { getPaymentMethodFromPolymorphicTransaction } from 'assets/js/pages/transactions/util';
import { AddressReadable } from 'components/pages/campaign-settings/campaign-auction/AddressReadable';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { rawDateFromServer, shortDateToDateTime } from 'helpers/dates';
import { getFormattedPhoneNumber } from 'helpers/formatting';
import { DateTime } from 'luxon';
import { twMerge } from 'tailwind-merge';
import { PolymorphicTransaction, Transaction } from 'types/domain';

interface IReadOnlyPaymentDetailsContext {
    transaction: PolymorphicTransaction;
}

const ReadOnlyPaymentDetailsContext = createContext<IReadOnlyPaymentDetailsContext>({
    transaction: {} as PolymorphicTransaction,
});

interface ReadOnlyPaymentDetailsProps extends ComponentPropsWithoutRef<'div'> {
    transaction: PolymorphicTransaction;
}

const ReadOnlyPaymentDetails = (props: ReadOnlyPaymentDetailsProps) => {
    const { className = '', ...rest } = props;
    const { transaction } = props;

    const providerProps = useMemo(() => ({ transaction }), [transaction]);

    return (
        <ReadOnlyPaymentDetailsContext.Provider value={providerProps}>
            <div className={twMerge('flex flex-col gap-2', className)} {...rest} />
        </ReadOnlyPaymentDetailsContext.Provider>
    );
};

interface ReadOnlyPaymentNameProps extends ComponentPropsWithoutRef<'div'> {}

const ReadOnlyPaymentName = (props: ReadOnlyPaymentNameProps) => {
    const { className = '', ...rest } = props;
    const { transaction } = useContext(ReadOnlyPaymentDetailsContext);

    if (!transaction.first_name) {
        return null;
    }

    return (
        <div className={twMerge('flex items-center gap-2', className)} {...rest}>
            <ReadOnlyPaymentDetailLabel>Name</ReadOnlyPaymentDetailLabel>
            <ReadOnlyPaymentDetailData>
                {transaction.first_name} {transaction.last_name}
            </ReadOnlyPaymentDetailData>
        </div>
    );
};

interface ReadOnlyPaymentPhoneProps extends ComponentPropsWithoutRef<'div'> {}

const ReadOnlyPaymentPhone = (props: ReadOnlyPaymentPhoneProps) => {
    const { className = '', ...rest } = props;
    const { transaction } = useContext(ReadOnlyPaymentDetailsContext);

    if (!transaction.phone) {
        return null;
    }

    return (
        <div className={twMerge('flex items-center gap-2', className)} {...rest}>
            <ReadOnlyPaymentDetailLabel>
                <p>Phone</p>
            </ReadOnlyPaymentDetailLabel>
            <ReadOnlyPaymentDetailData>
                <p>{getFormattedPhoneNumber(transaction.phone)}</p>
            </ReadOnlyPaymentDetailData>
        </div>
    );
};

interface ReadOnlyPaymentAddressProps extends ComponentPropsWithoutRef<'div'> {}

const ReadOnlyPaymentAddress = (props: ReadOnlyPaymentAddressProps) => {
    const { className = '', ...rest } = props;
    const { transaction } = useContext(ReadOnlyPaymentDetailsContext);

    if (!transaction?.address?.address_1) {
        return null;
    }

    return (
        <div className={twMerge('flex gap-2', className)} {...rest}>
            <ReadOnlyPaymentDetailLabel>
                <p>Billing address</p>
            </ReadOnlyPaymentDetailLabel>
            <ReadOnlyPaymentDetailData>
                <AddressReadable
                    className={'max-w-[250px] [&_p]:text-sm [&_p]:text-gray-600'}
                    address={transaction.address}
                />
            </ReadOnlyPaymentDetailData>
        </div>
    );
};

interface ReadOnlyPaymentEmailProps extends ComponentPropsWithoutRef<'div'> {}

const ReadOnlyPaymentEmail = (props: ReadOnlyPaymentEmailProps) => {
    const { className = '', ...rest } = props;
    const { transaction } = useContext(ReadOnlyPaymentDetailsContext);

    if (!transaction.email) {
        return null;
    }

    return (
        <div className={twMerge('flex items-center gap-2', className)} {...rest}>
            <ReadOnlyPaymentDetailLabel>Billing email</ReadOnlyPaymentDetailLabel>
            <ReadOnlyPaymentDetailData>{transaction.email}</ReadOnlyPaymentDetailData>
        </div>
    );
};

interface ReadOnlyPaymentMethodProps extends ComponentPropsWithoutRef<'div'> {}

const ReadOnlyPaymentMethod = (props: ReadOnlyPaymentMethodProps) => {
    const { className = '', ...rest } = props;
    const { transaction } = useContext(ReadOnlyPaymentDetailsContext);
    const { method } = getPaymentMethodFromPolymorphicTransaction(transaction);

    if (!method) {
        return null;
    }

    return (
        <div className={twMerge('flex items-center gap-2', className)} {...rest}>
            <ReadOnlyPaymentDetailLabel>
                <p>Payment method</p>
            </ReadOnlyPaymentDetailLabel>
            <ReadOnlyPaymentDetailData>
                <TransactionMethodPresentational transaction={transaction} />
            </ReadOnlyPaymentDetailData>
        </div>
    );
};

interface ReadOnlyPaymentCcExpireProps extends ComponentPropsWithoutRef<'div'> {}
const ReadOnlyPaymentCcExpire = (props: ReadOnlyPaymentCcExpireProps) => {
    const { className = '', ...rest } = props;
    const { transaction } = useContext(ReadOnlyPaymentDetailsContext);
    const { cc_expire } = getPaymentMethodFromPolymorphicTransaction(transaction);

    if (!cc_expire) {
        return null;
    }

    return (
        <div className={twMerge('flex items-center gap-2', className)} {...rest}>
            <ReadOnlyPaymentDetailLabel>
                <p>Exp.</p>
            </ReadOnlyPaymentDetailLabel>
            <ReadOnlyPaymentDetailData>
                <p>{shortDateToDateTime(cc_expire).toFormat('MM/yy')}</p>
            </ReadOnlyPaymentDetailData>
        </div>
    );
};

interface ReadOnlyPaymentDafProps extends ComponentPropsWithoutRef<'div'> {}

const ReadOnlyPaymentDaf = (props: ReadOnlyPaymentDafProps) => {
    const { className = '', ...rest } = props;
    const { transaction: polymorphicTransaction } = useContext(ReadOnlyPaymentDetailsContext);
    const { method } = getPaymentMethodFromPolymorphicTransaction(polymorphicTransaction);
    const transaction = polymorphicTransaction as Transaction;

    if (method !== 'donor_advised_fund' || !transaction.service_charge_id) {
        return null;
    }

    return (
        <div className={twMerge('flex items-center gap-2', className)} {...rest}>
            <ReadOnlyPaymentDetailLabel>
                <p>DAF Charge ID</p>
            </ReadOnlyPaymentDetailLabel>
            <ReadOnlyPaymentDetailData>
                <p className={'break-words'}>{transaction.service_charge_id}</p>
            </ReadOnlyPaymentDetailData>
        </div>
    );
};

interface ReadOnlyPaymentCompanyProps extends ComponentPropsWithoutRef<'div'> {}

const ReadOnlyPaymentCompany = (props: ReadOnlyPaymentCompanyProps) => {
    const { className = '', ...rest } = props;
    const { transaction } = useContext(ReadOnlyPaymentDetailsContext);

    if (!transaction.company) {
        return null;
    }

    return (
        <div className={twMerge('flex items-center gap-2', className)} {...rest}>
            <ReadOnlyPaymentDetailLabel>
                <p>Company</p>
            </ReadOnlyPaymentDetailLabel>
            <ReadOnlyPaymentDetailData>
                <p>{transaction.company}</p>
            </ReadOnlyPaymentDetailData>
        </div>
    );
};

interface ReadOnlyPaymentCheckNumberProps extends ComponentPropsWithoutRef<'div'> {}

const ReadOnlyPaymentCheckNumber = (props: ReadOnlyPaymentCheckNumberProps) => {
    const { className = '', ...rest } = props;
    const { transaction } = useContext(ReadOnlyPaymentDetailsContext);

    if (!transaction.check_number) {
        return null;
    }

    return (
        <div className={twMerge('flex items-center gap-2', className)} {...rest}>
            <ReadOnlyPaymentDetailLabel>
                <p>Check number</p>
            </ReadOnlyPaymentDetailLabel>
            <ReadOnlyPaymentDetailData>
                <p>{transaction.check_number}</p>
            </ReadOnlyPaymentDetailData>
        </div>
    );
};

interface ReadOnlyPaymentCheckDepositedDateProps extends ComponentPropsWithoutRef<'div'> {}

const ReadOnlyPaymentCheckDepositedDate = (props: ReadOnlyPaymentCheckDepositedDateProps) => {
    const { className = '', ...rest } = props;
    const { timezone } = useCurrentAccount();
    const { transaction } = useContext(ReadOnlyPaymentDetailsContext);

    if (!transaction.check_deposited_at) {
        return null;
    }

    return (
        <div className={twMerge('flex items-center gap-2', className)} {...rest}>
            <ReadOnlyPaymentDetailLabel>
                <p>Deposit date</p>
            </ReadOnlyPaymentDetailLabel>
            <ReadOnlyPaymentDetailData>
                <p>
                    {rawDateFromServer(transaction.check_deposited_at)
                        .setZone(timezone)
                        .toLocaleString(DateTime.DATE_FULL)}
                </p>
            </ReadOnlyPaymentDetailData>
        </div>
    );
};

interface ReadOnlyPaymentDetailLabelProps extends ComponentPropsWithoutRef<'div'> {}
const ReadOnlyPaymentDetailLabel = (props: ReadOnlyPaymentDetailLabelProps) => {
    const { className = '', ...rest } = props;

    return <div className={twMerge('w-[120px] text-sm font-medium', className)} {...rest} />;
};

interface ReadOnlyPaymentDetailDataProps extends ComponentPropsWithoutRef<'div'> {}
const ReadOnlyPaymentDetailData = (props: ReadOnlyPaymentDetailDataProps) => {
    const { className = '', ...rest } = props;

    return <div className={twMerge('text-sm text-gray-600', className)} {...rest} />;
};

const Root = ReadOnlyPaymentDetails;
const Name = ReadOnlyPaymentName;
const Phone = ReadOnlyPaymentPhone;
const Email = ReadOnlyPaymentEmail;
const Address = ReadOnlyPaymentAddress;
const Company = ReadOnlyPaymentCompany;
const Method = ReadOnlyPaymentMethod;
const CcExpire = ReadOnlyPaymentCcExpire;
const Daf = ReadOnlyPaymentDaf;
const CheckNumber = ReadOnlyPaymentCheckNumber;
const CheckDepositedDate = ReadOnlyPaymentCheckDepositedDate;
const Label = ReadOnlyPaymentDetailLabel;
const Data = ReadOnlyPaymentDetailData;

export {
    ReadOnlyPaymentDetails,
    ReadOnlyPaymentName,
    ReadOnlyPaymentPhone,
    ReadOnlyPaymentEmail,
    ReadOnlyPaymentAddress,
    ReadOnlyPaymentCompany,
    ReadOnlyPaymentMethod,
    ReadOnlyPaymentCcExpire,
    ReadOnlyPaymentDaf,
    ReadOnlyPaymentCheckNumber,
    ReadOnlyPaymentCheckDepositedDate,
    ReadOnlyPaymentDetailLabel,
    ReadOnlyPaymentDetailData,
    //
    Root,
    Name,
    Phone,
    Email,
    Address,
    Company,
    Method,
    CcExpire,
    Daf,
    CheckNumber,
    CheckDepositedDate,
    Label,
    Data,
};
export type { IReadOnlyPaymentDetailsContext };
